/* Global user state with an event bus.
 *
 * Modules can `subscribeUser(fn)` to react to login/logout/trial changes.
 * The state is hydrated once at app boot via `loadUser()`. Anything that
 * changes the user (login, logout, verify-email, billing webhook→reload)
 * calls `setUser(...)` and listeners fire.
 */

import { me, type User } from './client';

let _user: User | null = null;
let _hydrated = false;
const _listeners = new Set<(u: User | null) => void>();

export function getUser(): User | null {
  return _user;
}

export function isHydrated(): boolean {
  return _hydrated;
}

export function setUser(u: User | null): void {
  _user = u;
  _hydrated = true;
  _listeners.forEach((fn) => {
    try { fn(_user); } catch (e) { console.error('user listener threw', e); }
  });
  // Also dispatch a window event so modules outside the auth/ dir can hook in
  window.dispatchEvent(new CustomEvent('lp-user-changed', { detail: _user }));
}

export function subscribeUser(fn: (u: User | null) => void): () => void {
  _listeners.add(fn);
  if (_hydrated) fn(_user);
  return () => _listeners.delete(fn);
}

export async function loadUser(): Promise<User | null> {
  try {
    const u = await me();
    setUser(u);
    return u;
  } catch (err: any) {
    if (err?.status === 401) {
      setUser(null);
      return null;
    }
    // Network down? Don't lock the UI — set null but log.
    console.warn('loadUser failed', err);
    setUser(null);
    return null;
  }
}
